import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Circle, useGoogleMap } from '@react-google-maps/api';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  LinearProgress,
  FormControlLabel,
  Switch,
  Collapse,
  IconButton,
  TextField,
} from '@mui/material';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../services/api';
import {
  deviceMapCoordinates,
  isCapDevice,
  isNodeDevice,
  hasPublicWifi,
  createRrmMarkerDiv,
} from '../utils/publicWifiRrmPlanUi';
import {
  analyzeFieldSurveyChannelOverlaps,
  buildLocationForeignOverlapIndex,
} from '../utils/fieldSurveyChannelUtils';
import { haversineDistanceM } from '../utils/channelMapUtils';
import {
  buildLampCoveragesFromSurvey,
  buildOverviewCoveragePoints,
  countCoverageBelowThreshold,
  DEFAULT_COVERAGE_MIN_RSSI_DBM,
  filterCoveragePointsAboveThreshold,
  formatSampleRssiLabel,
  lampCoverageMapPoints,
  normalizeMac,
  resolveCoverageHeatmapPointStyle,
} from '../utils/fieldSurveyLampCoverage';
import { MAP_UI } from '../theme/mapViewUi';

const defaultCenter = { lat: 32.0853, lng: 34.7818 };

function createSurveySampleMarkerDiv({
  fillColor,
  strokeColor,
  label,
  showLabel = false,
  opacity = 1,
}) {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer;';
  wrap.title = label;

  const dot = document.createElement('div');
  const sizePx = showLabel ? 12 : 10;
  dot.style.cssText = `
    width: ${sizePx}px;
    height: ${sizePx}px;
    border-radius: 50%;
    background: ${fillColor};
    border: 2px solid ${strokeColor};
    opacity: ${opacity};
    box-shadow: 0 0 0 1px rgba(255,255,255,0.85);
  `;
  wrap.appendChild(dot);

  if (showLabel) {
    const text = document.createElement('div');
    text.textContent = label.replace(' dBm', '');
    text.style.cssText = `
      margin-top: 2px;
      font-size: 11px;
      font-weight: 800;
      line-height: 1;
      color: #111;
      text-shadow: 0 0 3px #fff, 0 0 3px #fff, 0 1px 0 #fff;
      white-space: nowrap;
    `;
    wrap.appendChild(text);
  }

  return wrap;
}

function formatSurveyLabel(item) {
  const submitted = item?.submitted_at
    ? new Date(item.submitted_at).toLocaleString()
    : 'Unknown time';
  const ssid = item?.session?.targetSsid || item?.session?.target_ssid || 'Survey';
  const samples = item?.stats?.sample_count ?? item?.stats?.SampleCount ?? 0;
  return `${submitted} — ${ssid} (${samples} samples)`;
}

function formatChannelLabel(entry) {
  const ch = entry?.channel;
  const freq = entry?.frequencyMHz ?? entry?.frequency_mhz;
  if (ch && freq) return `Ch ${ch} (${freq} MHz)`;
  if (ch) return `Ch ${ch}`;
  if (freq) return `${freq} MHz`;
  return '—';
}

function overlapPairRowKey(pair) {
  return `${pair.clusterId}::${pair.target.bssid}::${pair.foreign.bssid}`;
}

const AdvancedMarker = ({ position, content, onClick, title, zIndex }) => {
  const markerRef = React.useRef(null);
  const clickListenerRef = React.useRef(null);
  const onClickRef = React.useRef(onClick);
  const map = useGoogleMap();

  onClickRef.current = onClick;

  const attachClickListener = React.useCallback((marker) => {
    if (!marker || !window.google?.maps?.event) return;
    if (clickListenerRef.current) {
      window.google.maps.event.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }
    clickListenerRef.current = marker.addListener('click', () => {
      onClickRef.current?.();
    });
  }, []);

  React.useEffect(() => {
    if (!window.google?.maps?.marker?.AdvancedMarkerElement || !map) {
      return undefined;
    }
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position,
      content,
      title,
      map,
      zIndex,
    });
    markerRef.current = marker;
    attachClickListener(marker);
    return () => {
      if (clickListenerRef.current) {
        window.google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, attachClickListener]);

  React.useEffect(() => {
    if (markerRef.current && position) {
      markerRef.current.position = position;
    }
  }, [position]);

  React.useEffect(() => {
    if (markerRef.current && content) {
      markerRef.current.content = content;
    }
  }, [content]);

  React.useEffect(() => {
    if (markerRef.current) {
      markerRef.current.title = title || '';
    }
  }, [title]);

  React.useEffect(() => {
    if (markerRef.current && zIndex != null) {
      markerRef.current.zIndex = zIndex;
    }
  }, [zIndex]);

  return null;
};

const SurveySampleMarker = ({
  position,
  fillColor,
  strokeColor,
  label,
  showLabel,
  opacity,
  onClick,
  zIndex,
}) => {
  const markerRef = React.useRef(null);
  const clickListenerRef = React.useRef(null);
  const onClickRef = React.useRef(onClick);
  const map = useGoogleMap();

  onClickRef.current = onClick;

  const content = React.useMemo(
    () => createSurveySampleMarkerDiv({
      fillColor,
      strokeColor,
      label,
      showLabel,
      opacity,
    }),
    [fillColor, strokeColor, label, showLabel, opacity],
  );

  React.useEffect(() => {
    if (!window.google?.maps?.marker?.AdvancedMarkerElement || !map) {
      return undefined;
    }
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position,
      content,
      title: label,
      map,
      zIndex,
    });
    markerRef.current = marker;
    clickListenerRef.current = marker.addListener('click', () => {
      onClickRef.current?.();
    });
    return () => {
      if (clickListenerRef.current) {
        window.google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
    // position updated in dedicated effect below — omit from deps to avoid marker teardown on new object refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, content, label, zIndex]);

  React.useEffect(() => {
    if (markerRef.current && position) {
      markerRef.current.position = position;
    }
  }, [position]);

  React.useEffect(() => {
    if (markerRef.current) {
      markerRef.current.content = content;
      markerRef.current.title = label || '';
    }
  }, [content, label]);

  React.useEffect(() => {
    if (markerRef.current && zIndex != null) {
      markerRef.current.zIndex = zIndex;
    }
  }, [zIndex]);

  return null;
};

const FieldSurveyTab = ({
  devices,
  publicWifiMap = {},
  isLoaded,
  loadError,
  mapColorScheme,
  isActive,
  canMutate = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const [surveyList, setSurveyList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [surveyDoc, setSurveyDoc] = useState(null);
  const [surveySamples, setSurveySamples] = useState([]);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(null);
  const [selectedDeviceSerial, setSelectedDeviceSerial] = useState(null);
  const [selectedConflictKey, setSelectedConflictKey] = useState(null);
  const [selectedLampBssid, setSelectedLampBssid] = useState(null);
  const [showLampCoverage, setShowLampCoverage] = useState(true);
  const [showSatellite, setShowSatellite] = useState(false);
  const [minCoverageRssiDbm, setMinCoverageRssiDbm] = useState(DEFAULT_COVERAGE_MIN_RSSI_DBM);
  const [minCoverageRssiInput, setMinCoverageRssiInput] = useState(String(DEFAULT_COVERAGE_MIN_RSSI_DBM));
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const mapRef = useRef(null);
  const mapSectionRef = useRef(null);
  const hasFittedRef = useRef(false);

  const refreshSurveyList = useCallback(() => {
    setListLoading(true);
    setListError(null);
    return api.listFieldSurveys()
      .then((data) => {
        const surveys = data?.surveys || [];
        setSurveyList(surveys);
        if (surveys.length > 0) {
          setSelectedSurveyId((prev) => {
            if (prev && surveys.some((s) => s.id === prev)) return prev;
            return surveys[0].id;
          });
        } else {
          setSelectedSurveyId('');
        }
        return surveys;
      })
      .catch((err) => {
        setListError(err?.message || String(err));
        return [];
      })
      .finally(() => setListLoading(false));
  }, []);

  const devicesForChannelAnalysis = useMemo(() => (devices || []).map((device) => {
    const serial = (device?.serial_number || '').toLowerCase();
    const entry = publicWifiMap[serial];
    if (!entry?.interfaces?.length) return device;
    const pw = device?.public_wifi || {};
    if (pw.interfaces?.length || pw.Interfaces?.length) return device;
    return { ...device, public_wifi: { ...pw, interfaces: entry.interfaces } };
  }), [devices, publicWifiMap]);

  const channelAnalysis = useMemo(
    () => analyzeFieldSurveyChannelOverlaps(surveySamples, { devices: devicesForChannelAnalysis }),
    [devicesForChannelAnalysis, surveySamples],
  );

  const lampCoverages = useMemo(
    () => buildLampCoveragesFromSurvey(surveySamples, devices, publicWifiMap),
    [surveySamples, devices, publicWifiMap],
  );

  const matchedLampCount = useMemo(
    () => lampCoverages.filter((l) => l.matchedDevice).length,
    [lampCoverages],
  );

  const selectedConflictPair = useMemo(() => {
    if (!selectedConflictKey) return null;
    return channelAnalysis.overlapPairs.find((pair) => overlapPairRowKey(pair) === selectedConflictKey) || null;
  }, [selectedConflictKey, channelAnalysis.overlapPairs]);

  const highlightedLampBssid = useMemo(() => {
    if (selectedLampBssid) return selectedLampBssid;
    if (!selectedConflictPair?.target?.bssid) return null;
    return normalizeMac(selectedConflictPair.target.bssid) || null;
  }, [selectedLampBssid, selectedConflictPair]);

  const highlightedLamp = useMemo(
    () => lampCoverages.find((l) => l.bssid === highlightedLampBssid) || null,
    [lampCoverages, highlightedLampBssid],
  );

  const isLampListHeatmapActive = Boolean(selectedLampBssid && !selectedConflictKey);

  const overviewCoveragePoints = useMemo(
    () => (isLampListHeatmapActive ? [] : buildOverviewCoveragePoints(lampCoverages, minCoverageRssiDbm)),
    [lampCoverages, minCoverageRssiDbm, isLampListHeatmapActive],
  );

  const belowThresholdSampleCount = useMemo(
    () => lampCoverages.reduce(
      (sum, lamp) => sum + countCoverageBelowThreshold(lamp.heatmapPoints, minCoverageRssiDbm),
      0,
    ),
    [lampCoverages, minCoverageRssiDbm],
  );

  const handleMinCoverageRssiChange = useCallback((event) => {
    const raw = event.target.value;
    if (raw === '' || raw === '-' || /^-?\d*$/.test(raw)) {
      setMinCoverageRssiInput(raw);
    }
  }, []);

  const commitMinCoverageRssiInput = useCallback(() => {
    const parsed = Number(minCoverageRssiInput);
    if (!Number.isFinite(parsed)) {
      setMinCoverageRssiInput(String(minCoverageRssiDbm));
      return;
    }
    const clamped = Math.max(-100, Math.min(-30, Math.round(parsed)));
    setMinCoverageRssiDbm(clamped);
    setMinCoverageRssiInput(String(clamped));
  }, [minCoverageRssiInput, minCoverageRssiDbm]);

  const handleMinCoverageRssiKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitMinCoverageRssiInput();
      event.target.blur();
    }
  }, [commitMinCoverageRssiInput]);

  const locationSummaries = useMemo(() => {
    const base = (surveyDoc?.location_summaries || []).filter(
      (loc) => Number.isFinite(loc.latitude) && Number.isFinite(loc.longitude),
    );
    if (!surveySamples.length) return base;
    return buildLocationForeignOverlapIndex(base, channelAnalysis);
  }, [surveyDoc, surveySamples, channelAnalysis]);

  const mapDevices = useMemo(() => {
    if (!devices?.length) return [];
    const list = [];
    const seen = new Set();
    const surveySerials = new Set(
      lampCoverages
        .map((lamp) => (lamp.serial || '').toLowerCase())
        .filter(Boolean),
    );

    const push = (device, label, markerColor) => {
      const serial = (device.serial_number || '').toLowerCase();
      if (!serial || seen.has(serial)) return;
      const coords = deviceMapCoordinates(device);
      if (!coords) return;
      seen.add(serial);
      list.push({
        ...device,
        lat: coords.lat,
        lng: coords.lng,
        markerLabel: label,
        markerColor,
      });
    };
    devices.forEach((device) => {
      if (isCapDevice(device)) {
        push(device, 'C', MAP_UI.accent);
      } else if (isNodeDevice(device) && hasPublicWifi(device)) {
        push(device, 'W', MAP_UI.rrmPlanMarker);
      } else if (isNodeDevice(device) && surveySerials.has((device.serial_number || '').toLowerCase())) {
        push(device, 'N', MAP_UI.deviceOnline);
      }
    });
    return list;
  }, [devices, lampCoverages]);

  const mapCenter = useMemo(() => {
    const points = mapDevices.map((d) => ({ lat: d.lat, lng: d.lng }));
    lampCoverages.forEach((lamp) => {
      lampCoverageMapPoints(lamp).forEach((p) => {
        points.push({ lat: p.lat, lng: p.lng });
      });
    });
    if (points.length === 0) return defaultCenter;
    const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
    const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
    return { lat: avgLat, lng: avgLng };
  }, [mapDevices, lampCoverages]);

  const fitMapToLampCoverage = useCallback((lamp) => {
    const map = mapRef.current;
    const points = lampCoverageMapPoints(lamp);
    if (!map || !window.google?.maps || !points.length) return false;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 56);
    return true;
  }, []);

  const fitMapToContent = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoint = false;
    mapDevices.forEach((d) => {
      bounds.extend({ lat: d.lat, lng: d.lng });
      hasPoint = true;
    });
    if (lampCoverages.length > 0) {
      const points = isLampListHeatmapActive
        ? lampCoverages.flatMap((lamp) => filterCoveragePointsAboveThreshold(lamp.heatmapPoints, minCoverageRssiDbm))
        : overviewCoveragePoints.length
          ? overviewCoveragePoints
          : lampCoverages.flatMap((lamp) => lampCoverageMapPoints(lamp));
      points.forEach((p) => {
        bounds.extend({ lat: p.lat, lng: p.lng });
        hasPoint = true;
      });
    }
    if (!hasPoint) return;
    map.fitBounds(bounds, 48);
  }, [mapDevices, lampCoverages, minCoverageRssiDbm, isLampListHeatmapActive, overviewCoveragePoints]);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (!hasFittedRef.current) {
      hasFittedRef.current = true;
      window.requestAnimationFrame(() => fitMapToContent());
    }
  }, [fitMapToContent]);

  const focusConflictOnMap = useCallback((pair) => {
    const lat = pair?.clusterLat;
    const lng = pair?.clusterLng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    setSelectedConflictKey(overlapPairRowKey(pair));
    setSelectedDeviceSerial(null);

    const targetBssid = normalizeMac(pair?.target?.bssid);
    const matchingLamp = targetBssid
      ? lampCoverages.find((lamp) => lamp.bssid === targetBssid)
      : null;
    setSelectedLampBssid(matchingLamp?.bssid || null);

    let bestIdx = -1;
    let bestDist = Infinity;
    locationSummaries.forEach((loc, idx) => {
      const d = haversineDistanceM(lat, lng, loc.latitude, loc.longitude);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = idx;
      }
    });
    setSelectedLocationIdx(bestIdx >= 0 ? bestIdx : null);

    const map = mapRef.current;
    if (map && window.google?.maps) {
      if (matchingLamp) {
        fitMapToLampCoverage(matchingLamp);
      } else {
        map.panTo({ lat, lng });
        const zoom = map.getZoom();
        if (!Number.isFinite(zoom) || zoom < 18) {
          map.setZoom(18);
        }
      }
    }

    window.requestAnimationFrame(() => {
      mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [locationSummaries, lampCoverages, fitMapToLampCoverage]);

  const focusLampOnMap = useCallback((coverage) => {
    if (!lampCoverageMapPoints(coverage).length) return;
    setSelectedLampBssid(coverage.bssid);
    setSelectedLocationIdx(null);
    setSelectedDeviceSerial(null);
    setSelectedConflictKey(null);

    fitMapToLampCoverage(coverage);

    window.requestAnimationFrame(() => {
      mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [fitMapToLampCoverage]);

  useEffect(() => {
    if (!isActive) return undefined;
    refreshSurveyList();
    return undefined;
  }, [isActive, refreshSurveyList]);

  useEffect(() => {
    if (!isActive || !selectedSurveyId) {
      setSurveyDoc(null);
      setSurveySamples([]);
      return undefined;
    }
    let cancelled = false;
    setSurveyLoading(true);
    setSamplesLoading(true);
    setSelectedLocationIdx(null);
    setSelectedDeviceSerial(null);
    setSelectedConflictKey(null);
    setSelectedLampBssid(null);

    Promise.all([
      api.getFieldSurvey(selectedSurveyId),
      api.getFieldSurveySamples(selectedSurveyId),
    ])
      .then(([doc, samplesResp]) => {
        if (cancelled) return;
        setSurveyDoc(doc);
        setSurveySamples(samplesResp?.samples || []);
        hasFittedRef.current = false;
      })
      .catch((err) => {
        if (!cancelled) {
          setSurveyDoc(null);
          setSurveySamples([]);
          setListError(err?.message || String(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSurveyLoading(false);
          setSamplesLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [isActive, selectedSurveyId]);

  useEffect(() => {
    if (surveyDoc && mapRef.current) {
      window.requestAnimationFrame(() => fitMapToContent());
    }
  }, [surveyDoc, fitMapToContent]);

  const selectedLampDevice = useMemo(() => {
    if (!selectedLampBssid) return null;
    const lamp = lampCoverages.find((l) => l.bssid === selectedLampBssid);
    if (!lamp?.serial) return null;
    return mapDevices.find((d) => (d.serial_number || '').toLowerCase() === lamp.serial) || null;
  }, [selectedLampBssid, lampCoverages, mapDevices]);

  const deviceMarkerContent = useCallback((device, isHighlighted = false) => {
    const isCap = isCapDevice(device);
    const color = device.markerColor || (isCap ? MAP_UI.accent : MAP_UI.rrmPlanMarker);
    const label = device.markerLabel || (isCap ? 'C' : 'W');
    const inner = createRrmMarkerDiv(
      color,
      label,
      isHighlighted ? { size: 34, borderWidth: '3px' } : undefined,
    );
    if (!isHighlighted) return inner;
    // Wrap with an orange ring embedded directly in the marker element
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:relative;display:flex;align-items:center;justify-content:center;';
    const ring = document.createElement('div');
    ring.style.cssText = [
      'position:absolute;',
      'width:52px;height:52px;',
      'border-radius:50%;',
      'border:3px solid #ff6d00;',
      'box-shadow:0 0 0 2px rgba(255,109,0,0.25),0 0 10px rgba(255,109,0,0.5);',
      'pointer-events:none;',
    ].join('');
    wrap.appendChild(ring);
    wrap.appendChild(inner);
    return wrap;
  }, []);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadProgress(10);
    try {
      const text = await file.text();
      setUploadProgress(40);
      const payload = JSON.parse(text);
      if (!payload?.wifiScanSamples?.length) {
        throw new Error('Invalid survey file: wifiScanSamples is required');
      }
      setUploadProgress(70);
      const created = await api.uploadFieldSurvey(payload);
      setUploadProgress(100);
      enqueueSnackbar(`Survey uploaded (${created?.stats?.sample_count ?? payload.wifiScanSamples.length} samples)`, { variant: 'success' });
      const surveys = await refreshSurveyList();
      if (created?.id) {
        setSelectedSurveyId(created.id);
      } else if (surveys[0]?.id) {
        setSelectedSurveyId(surveys[0].id);
      }
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to upload survey', { variant: 'error' });
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const selectedLocation = selectedLocationIdx != null
    ? locationSummaries[selectedLocationIdx]
    : null;

  const selectedDeviceConflict = selectedDeviceSerial
    ? channelAnalysis.deviceConflictDetails.find((d) => d.serial === selectedDeviceSerial)
    : null;

  const mapBusy = !isLoaded || listLoading || surveyLoading || samplesLoading;

  return (
    <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationSearchingIcon color="primary" />
              Field Survey
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Walk-path Wi‑Fi scan overlay. Co-channel conflicts are detected only where our public Wifi
              and a foreign AP were heard within {channelAnalysis.proximityM ?? 60} m on the same channel/frequency.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 260 }} disabled={listLoading || surveyList.length === 0}>
              <InputLabel>Survey run</InputLabel>
              <Select
                value={selectedSurveyId}
                label="Survey run"
                onChange={(e) => setSelectedSurveyId(e.target.value)}
              >
                {surveyList.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {formatSurveyLabel(item)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              startIcon={listLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={refreshSurveyList}
              disabled={listLoading}
            >
              Refresh
            </Button>
            {canMutate && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  hidden
                  onChange={handleFileSelected}
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                  onClick={handleUploadClick}
                  disabled={uploading}
                >
                  Upload JSON
                </Button>
              </>
            )}
          </Stack>
        </Stack>
        {uploading && uploadProgress != null && (
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 2 }} />
        )}
        {listError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setListError(null)}>{listError}</Alert>
        )}
        {surveyDoc && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Chip size="small" label={`Target SSID: ${surveyDoc.session?.targetSsid || surveyDoc.session?.target_ssid || '—'}`} />
            <Chip size="small" label={`${surveyDoc.stats?.sample_count ?? 0} scan samples`} />
            <Chip size="small" label={`${surveyDoc.stats?.unique_locations ?? 0} GPS points`} />
            <Chip size="small" label={`${channelAnalysis.foreignAps.length} foreign APs (unique)`} />
            <Chip
              size="small"
              color={channelAnalysis.overlapLocationCount > 0 ? 'error' : 'default'}
              label={`${channelAnalysis.overlapLocationCount} walk area(s) with co-channel conflict`}
            />
            {channelAnalysis.overlapPairs.length > 0 && (
              <Chip size="small" variant="outlined" label={`${channelAnalysis.overlapPairs.length} target↔foreign pair(s)`} />
            )}
            {lampCoverages.length > 0 && (
              <>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${lampCoverages.length} target AP(s) heard · ${matchedLampCount} mapped to devices`}
                />
                <Chip
                  size="small"
                  color={belowThresholdSampleCount > 0 ? 'error' : 'default'}
                  variant="outlined"
                  label={`${belowThresholdSampleCount} sample(s) below ${minCoverageRssiDbm} dBm`}
                />
              </>
            )}
          </Stack>
        )}
      </Box>

      {surveyDoc && !samplesLoading && channelAnalysis.overlapPairs.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'rgba(211, 47, 47, 0.04)' }}>
          <Box
            onClick={() => setConflictsOpen((o) => !o)}
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setConflictsOpen((o) => !o); }}
              aria-label={conflictsOpen ? 'Collapse co-channel conflicts' : 'Expand co-channel conflicts'}
              sx={{
                transform: conflictsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
              Co-channel conflicts at survey walk areas (Our public Wifi vs foreign AP, within {channelAnalysis.proximityM} m)
            </Typography>
            <Chip size="small" color="error" label={`${channelAnalysis.overlapPairs.length} pair(s)`} />
          </Box>
          <Collapse in={conflictsOpen}>
            <Box sx={{ px: 2, pb: 1.5 }}>
              <TableContainer sx={{ maxHeight: 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Target (Our public Wifi)</TableCell>
                      <TableCell>Target channel</TableCell>
                      <TableCell>Foreign SSID</TableCell>
                      <TableCell>Foreign channel</TableCell>
                      <TableCell align="right">Foreign RSSI</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {channelAnalysis.overlapPairs.slice(0, 50).map((pair) => {
                      const rowKey = overlapPairRowKey(pair);
                      return (
                      <TableRow
                        key={rowKey}
                        hover
                        selected={selectedConflictKey === rowKey}
                        onClick={() => focusConflictOnMap(pair)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{pair.target.ssid || pair.target.bssid || 'Target'}</TableCell>
                        <TableCell>{formatChannelLabel(pair.target)} ({pair.target.band})</TableCell>
                        <TableCell>{pair.foreign.ssid || pair.foreign.bssid || 'Hidden'}</TableCell>
                        <TableCell>{formatChannelLabel(pair.foreign)} ({pair.foreign.band})</TableCell>
                        <TableCell align="right">{pair.foreign.rssiDbm ?? '—'} dBm</TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {channelAnalysis.overlapPairs.length > 50 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Showing first 50 of {channelAnalysis.overlapPairs.length} overlap pairs. Click a row to highlight our public Wifi coverage and conflict details.
                </Typography>
              )}
              {channelAnalysis.overlapPairs.length <= 50 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Click a row to highlight our public Wifi coverage involved and show conflict details on the map.
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      <Box ref={mapSectionRef} sx={{ position: 'relative', width: '100%', height: { xs: '400px', md: '650px' } }}>
        {loadError ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Alert severity="info" sx={{ m: 2 }}>Map unavailable.</Alert>
          </Box>
        ) : mapBusy ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : surveyList.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3, gap: 2 }}>
            <Alert severity="info" sx={{ maxWidth: 520 }}>
              No field surveys yet. Upload a JSON export from the field survey app{canMutate ? ', or POST to `/public-wifi/field-survey`' : ''}.
            </Alert>
            {canMutate && (
              <Button variant="contained" startIcon={<UploadFileIcon />} onClick={handleUploadClick} disabled={uploading}>
                Upload survey JSON
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Box sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              zIndex: 1000,
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: 1,
              px: 1.5,
              py: 1,
              boxShadow: 1,
              maxWidth: 320,
            }}
            >
              <Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 0.5 }}>
                Legend
              </Typography>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: MAP_UI.rrmPlanMarker }} />
                  <Typography variant="caption">Public WiFi enabled (W) / CAP (C)</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: MAP_UI.deviceOnline }} />
                  <Typography variant="caption">Survey device, no Public WiFi (N)</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: MAP_UI.rrmPlanMarker, border: '3px solid #ff6d00', flexShrink: 0 }} />
                  <Typography variant="caption">Selected AP device (orange ring)</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: MAP_UI.throughputExcellent, border: '1px solid #fff' }} />
                  <Typography variant="caption">Samples ≥ min RSSI — best RSSI per location (hover = RSSI)</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: MAP_UI.throughputExcellent, border: '2px solid #fff' }} />
                  <Typography variant="caption">Selected AP — all samples + RSSI label</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#e53935', border: '1px solid #fff' }} />
                  <Typography variant="caption">Below {minCoverageRssiDbm} dBm (selected AP)</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9e9e9e', border: '1px solid #616161', opacity: 0.4 }} />
                  <Typography variant="caption">Other APs (background)</Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Satellite toggle — top-right */}
            <Box sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1000,
              bgcolor: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(4px)',
              borderRadius: 1,
              px: 1.25,
              py: 0.5,
              boxShadow: 1,
            }}>
              <FormControlLabel
                control={<Switch size="small" checked={showSatellite} onChange={(e) => setShowSatellite(e.target.checked)} />}
                label={<Typography variant="caption">Satellite</Typography>}
                sx={{ m: 0 }}
              />
            </Box>

            {/* Info overlay panel — top-right, below satellite toggle */}
            {(highlightedLamp || (selectedLocation && !highlightedLamp) || selectedDeviceConflict) && (
              <Box sx={{
                position: 'absolute',
                top: 56,
                right: 12,
                zIndex: 1000,
                bgcolor: 'rgba(255,255,255,0.88)',
                backdropFilter: 'blur(6px)',
                borderRadius: 1,
                px: 1.5,
                py: 1,
                boxShadow: 2,
                maxWidth: 280,
              }}>
                {highlightedLamp && (() => {
                  const lamp = highlightedLamp;
                  return (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {lamp.matchedDevice ? (lamp.deviceName || lamp.serial) : 'Unknown AP'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ cursor: 'pointer', color: 'text.secondary', ml: 1, flexShrink: 0 }}
                          onClick={() => { setSelectedLampBssid(null); setSelectedConflictKey(null); setSelectedLocationIdx(null); }}
                        >
                          ✕
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', color: 'text.secondary' }}>
                        {lamp.bssidFormatted}
                        {lamp.radioName ? ` · ${lamp.radioName}` : ''}
                        {lamp.channel ? ` · Ch ${lamp.channel}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        RSSI min <strong>{lamp.worstRssiDbm ?? '—'}</strong>
                        {' / median '}
                        <strong>{lamp.medianRssiDbm ?? '—'}</strong>
                        {' / max '}
                        <strong>{lamp.bestRssiDbm ?? '—'}</strong>
                        {' dBm'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lamp.heatmapPoints?.length ?? 0} samples · {lamp.walkPointCount ?? 0} GPS points
                      </Typography>
                      {countCoverageBelowThreshold(lamp.heatmapPoints, minCoverageRssiDbm) > 0 && (
                        <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.25 }}>
                          {countCoverageBelowThreshold(lamp.heatmapPoints, minCoverageRssiDbm)} below {minCoverageRssiDbm} dBm
                        </Typography>
                      )}
                      {selectedConflictPair && (
                        <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.25 }}>
                          Co-channel: {selectedConflictPair.foreign.ssid || selectedConflictPair.foreign.bssid || 'foreign AP'} {formatChannelLabel(selectedConflictPair.foreign)}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
                {selectedLocation && !highlightedLamp && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Co-channel conflict area</Typography>
                      <Typography
                        variant="caption"
                        sx={{ cursor: 'pointer', color: 'text.secondary', ml: 1, flexShrink: 0 }}
                        onClick={() => { setSelectedLocationIdx(null); setSelectedConflictKey(null); }}
                      >
                        ✕
                      </Typography>
                    </Stack>
                    <Typography variant="body2">
                      Best RSSI: {selectedLocation.best_target_rssi_dbm ?? selectedLocation.bestTargetRssiDbm ?? '—'} dBm
                    </Typography>
                    <Typography variant="body2">
                      Foreign APs: {selectedLocation.foreignApCount ?? selectedLocation.other_sample_count ?? 0}
                    </Typography>
                    {(selectedLocation.overlappingForeign || []).slice(0, 4).map((entry) => (
                      <Typography key={`${entry.clusterId}-${entry.foreign.bssid}-${entry.target.bssid}`} variant="caption" display="block">
                        {entry.foreign.ssid || 'Hidden'} {formatChannelLabel(entry.foreign)} ↔ our {formatChannelLabel(entry.target)}
                      </Typography>
                    ))}
                  </Box>
                )}
                {selectedDeviceConflict && !highlightedLamp && !selectedLocation && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600 }}>
                        {selectedDeviceConflict.deviceName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ cursor: 'pointer', color: 'text.secondary', ml: 1, flexShrink: 0 }}
                        onClick={() => setSelectedDeviceSerial(null)}
                      >
                        ✕
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Within {channelAnalysis.proximityM} m of co-channel conflict
                    </Typography>
                    {selectedDeviceConflict.nearbyOverlaps.slice(0, 5).map((entry) => (
                      <Typography key={`${entry.clusterId}-${entry.foreign.bssid}`} variant="caption" display="block">
                        {formatChannelLabel(entry.target)} ↔ {entry.foreign.ssid || entry.foreign.bssid || 'Hidden'} {formatChannelLabel(entry.foreign)} ({entry.foreign.rssiDbm ?? '—'} dBm)
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={15}
              onLoad={handleMapLoad}
              mapTypeId={showSatellite ? 'satellite' : 'roadmap'}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                mapId: 'PUBLIC_WIFI_FIELD_SURVEY_MAP',
                colorScheme: mapColorScheme,
              }}
            >
              {mapDevices.map((device) => {
                const isHighlightedDevice = Boolean(
                  selectedLampDevice &&
                  (device.serial_number || '').toLowerCase() === selectedLampDevice.serial_number?.toLowerCase(),
                );
                return (
                  <AdvancedMarker
                    key={device.serial_number}
                    position={{ lat: device.lat, lng: device.lng }}
                    content={deviceMarkerContent(device, isHighlightedDevice)}
                    onClick={() => {
                      setSelectedDeviceSerial((device.serial_number || '').toLowerCase());
                      setSelectedLocationIdx(null);
                      setSelectedConflictKey(null);
                      setSelectedLampBssid(null);
                    }}
                    title={device.name || device.serial_number}
                    zIndex={isHighlightedDevice ? 11 : 2}
                  />
                );
              })}

              {showLampCoverage && !isLampListHeatmapActive && overviewCoveragePoints.map((point, pointIdx) => {
                const style = resolveCoverageHeatmapPointStyle(point, {
                  minRssiDbm: minCoverageRssiDbm,
                  isListSelected: false,
                  isHighlighted: false,
                  dimOthers: false,
                  lampColors: point.lamp?.colors,
                });
                return (
                  <SurveySampleMarker
                    key={`overview-${point.lat}-${point.lng}-${pointIdx}`}
                    position={{ lat: point.lat, lng: point.lng }}
                    fillColor={style.fillColor}
                    strokeColor={style.dotStrokeColor}
                    label={formatSampleRssiLabel(point.rssiDbm)}
                    showLabel={false}
                    opacity={0.85}
                    onClick={() => point.lamp && focusLampOnMap(point.lamp)}
                    zIndex={2}
                  />
                );
              })}

              {showLampCoverage && isLampListHeatmapActive && lampCoverages.map((lamp) => {
                const isDetailView = selectedLampBssid === lamp.bssid;
                const isBackground = !isDetailView;

                // Background BSSIDs in detail view: show only above-threshold samples as faint grey dots
                if (isBackground) {
                  const abovePoints = filterCoveragePointsAboveThreshold(lamp.heatmapPoints, minCoverageRssiDbm);
                  if (!abovePoints.length) return null;
                  return abovePoints.map((point, pointIdx) => (
                    <SurveySampleMarker
                      key={`lamp-${lamp.bssid}-bg-${pointIdx}`}
                      position={{ lat: point.lat, lng: point.lng }}
                      fillColor="#eeeeee"
                      strokeColor="#cccccc"
                      label={formatSampleRssiLabel(point.rssiDbm)}
                      showLabel={false}
                      opacity={0.10}
                      onClick={() => focusLampOnMap(lamp)}
                      zIndex={1}
                    />
                  ));
                }

                // Detail view for selected BSSID: all samples with RSSI labels + color
                if (isDetailView) {
                  return (lamp.heatmapPoints || []).map((point, pointIdx) => {
                    const style = resolveCoverageHeatmapPointStyle(point, {
                      minRssiDbm: minCoverageRssiDbm,
                      isListSelected: true,
                      isHighlighted: true,
                      dimOthers: false,
                      lampColors: lamp.colors,
                    });
                    return (
                      <SurveySampleMarker
                        key={`lamp-${lamp.bssid}-detail-${pointIdx}`}
                        position={{ lat: point.lat, lng: point.lng }}
                        fillColor={style.fillColor}
                        strokeColor={style.dotStrokeColor}
                        label={formatSampleRssiLabel(point.rssiDbm)}
                        showLabel
                        opacity={1}
                        onClick={() => focusLampOnMap(lamp)}
                        zIndex={style.belowThreshold ? 7 : 6}
                      />
                    );
                  });
                }

                return null;
              })}

              {selectedConflictPair && selectedLocation && (
                <Circle
                  center={{ lat: selectedLocation.latitude, lng: selectedLocation.longitude }}
                  radius={Math.max(selectedLocation.accuracy_meters || selectedLocation.accuracyMeters || 0, 8)}
                  options={{
                    fillColor: MAP_UI.deviceOffline,
                    fillOpacity: 0.12,
                    strokeColor: MAP_UI.deviceOffline,
                    strokeOpacity: 0.85,
                    strokeWeight: 2,
                    clickable: false,
                    zIndex: 4,
                  }}
                />
              )}

            </GoogleMap>
          </>
        )}
      </Box>

      {surveyDoc && !samplesLoading && lampCoverages.length > 0 && (
        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
              Public Wifi coverage — Our Devices
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={showLampCoverage}
                  onChange={(e) => setShowLampCoverage(e.target.checked)}
                />
              )}
              label="Show coverage on map"
            />
            <TextField
              size="small"
              type="text"
              inputMode="numeric"
              label="Min RSSI (dBm)"
              value={minCoverageRssiInput}
              onChange={handleMinCoverageRssiChange}
              onBlur={commitMinCoverageRssiInput}
              onKeyDown={handleMinCoverageRssiKeyDown}
              sx={{ width: 140 }}
            />
          </Stack>
          <TableContainer sx={{ maxHeight: 220 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>AP BSSID</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell align="right">Scan samples</TableCell>
                  <TableCell align="right">Walk points</TableCell>
                  <TableCell align="right">Below min</TableCell>
                  <TableCell align="right">Best RSSI</TableCell>
                  <TableCell>Channel</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lampCoverages.map((lamp) => {
                  const belowCount = countCoverageBelowThreshold(lamp.heatmapPoints, minCoverageRssiDbm);
                  return (
                  <TableRow
                    key={lamp.bssid}
                    hover
                    selected={highlightedLampBssid === lamp.bssid}
                    onClick={() => focusLampOnMap(lamp)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: lamp.colors.stroke, flexShrink: 0 }} />
                        {lamp.bssidFormatted}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {lamp.matchedDevice
                        ? (lamp.deviceName || lamp.serial)
                        : <Typography component="span" variant="body2" color="text.secondary">Not in project</Typography>}
                    </TableCell>
                    <TableCell align="right">{lamp.sampleCount}</TableCell>
                    <TableCell align="right">{lamp.walkPointCount}</TableCell>
                    <TableCell align="right">
                      {belowCount > 0 ? (
                        <Typography component="span" variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                          {belowCount}
                        </Typography>
                      ) : (
                        '0'
                      )}
                    </TableCell>
                    <TableCell align="right">{lamp.bestRssiDbm ?? '—'} dBm</TableCell>
                    <TableCell>{lamp.channel ? `Ch ${lamp.channel}` : '—'}</TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Each row is one of our devices the surveyor was connected to. Sample points below the min RSSI (or with no RSSI) are marked red on the heatmap. Click a row for a blue RSSI heatmap focused on that device.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FieldSurveyTab;
